/**
 * ReviewRequestDetail — the Response Generation Request detail page
 * inside the reviewer workspace (24 Aug 2026 flow).
 *
 * The reviewer NEVER touches the tenant frontend. Everything happens
 * here:
 *   1. Notice basic details + Accept / Reject (both confirm first).
 *   2. After Accept: "Prepare Required File/Attachment Checklist"
 *      (existing checklist engine) + "Open Notice" (drawer with the
 *      full notice record fetched through the reviewer-authorised
 *      detail endpoints).
 *   3. After the checklist exists: "Generate Share Link" mints the
 *      public no-login upload link. The link stays valid for a
 *      configurable window after its FIRST open (default 30 minutes,
 *      System Configuration). Manual WhatsApp / Email share buttons
 *      pre-fill the link for sending to the direct subscriber.
 *
 * Data: GET /managed-service/reviewer/{id}/workspace (one call).
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Input,
  Modal,
  Progress,
  Select,
  Skeleton,
  Space,
  Steps,
  Tag,
  Tooltip,
  Typography,
  message as antMessage,
} from "antd";
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CopyOutlined,
  FileDoneOutlined,
  FileSearchOutlined,
  FileTextOutlined,
  LinkOutlined,
  MailOutlined,
  PaperClipOutlined,
  ReloadOutlined,
  SwapOutlined,
  ThunderboltOutlined,
  UndoOutlined,
  WhatsAppOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { fetchData, postData } from "../../api/apiService";
import { CHECKLIST, DOCUMENTS, MANAGED_SERVICE } from "../../api/endpointMap";
import { NOTICE_FIELD_LABELS } from "./noticeFields";
import GenerateResponseFlow from "../../components/GenerateResponseFlow";
import { downloadAuthedFile } from "../../utils/downloadFile";
import {
  acceptRequestConfirm,
  captureRequest,
  disownRequestConfirm,
  releaseRequestConfirm,
} from "./routingActions";

const { Title, Paragraph, Text } = Typography;

const fmtDateTime = (iso) =>
  iso ? dayjs(iso).format("DD MMM YYYY, HH:mm") : "-";

// Routing state chips - same vocabulary as the dashboard.
const ROUTING_TAGS = {
  open: { color: "gold", label: "OPEN" },
  assigned: { color: "blue", label: "ASSIGNED" },
  accepted: { color: "green", label: "ACCEPTED" },
};

// Audit actions the timeline renders, in plain words.
const TIMELINE_LABELS = {
  created: "Request raised by the subscriber",
  assigned: "Assigned to a reviewer",
  accepted: "Accepted",
  disowned: "Disowned - opened to the portal's reviewers",
  released: "Released by an administrator - opened to the portal's reviewers",
  reassigned: "Reassigned by an administrator",
  // Kept for requests that were passed on before 07-10-2026, when the timeout
  // still handed the request to the next reviewer instead of opening it.
  reassigned_timeout: "Not accepted in time - passed to another reviewer",
  opened_timeout: "Not accepted in time - opened to the portal's reviewers",
  unrouted: "No reviewer available for this portal",
  awaiting_docs: "Waiting for the subscriber's documents",
  in_review: "Draft under review",
  delivered: "Delivered to the subscriber",
  rejected: "Closed by an administrator",
};

const STATUS_META = {
  requested: { color: "blue", label: "REQUESTED" },
  awaiting_docs: { color: "orange", label: "AWAITING DOCUMENTS" },
  ready_for_drafting: { color: "geekblue", label: "READY FOR DRAFTING" },
  in_review: { color: "purple", label: "IN REVIEW" },
  delivered: { color: "green", label: "DELIVERED" },
  cancelled: { color: "red", label: "REJECTED" },
};

// Human labels for the notice-basic-details block, per notice type.
// Field keys match the BE workspace serialization 1:1.


export default function ReviewRequestDetail() {
  const { requestId } = useParams();
  const navigate = useNavigate();

  const [ws, setWs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [acting, setActing] = useState(false);
  const [draftBusy, setDraftBusy] = useState(false);
  const [generateFlowOpen, setGenerateFlowOpen] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [minting, setMinting] = useState(false);
  // The raw share URL is returned exactly ONCE at mint time (only its
  // hash is stored server-side). To survive page refreshes it is also
  // cached in THIS browser's localStorage, keyed by the share row id -
  // the workspace payload's share_id validates the cache, so minting a
  // new link (here or elsewhere) makes a stale cached URL unusable.
  const [shareUrl, setShareUrl] = useState(null);

  // Which of the notice's documents is showing in the drawer's preview
  // pane (presigned inline PDF url). Null = nothing previewable.
  // Send-via-WhatsApp modal (approved template through the Meta API, as
  // opposed to the manual wa.me share). Number pre-fills from the
  // subscriber who raised the request.
  const [waSendOpen, setWaSendOpen] = useState(false);
  const [waSendTo, setWaSendTo] = useState("");
  const [waSending, setWaSending] = useState(false);

  const shareCacheKey = (checklistId) => `checklist_share_url:${checklistId}`;

  const rememberShareUrl = (checklistId, shareId, url) => {
    try {
      localStorage.setItem(
        shareCacheKey(checklistId),
        JSON.stringify({ share_id: shareId, url }),
      );
    } catch {
      /* storage unavailable - the in-memory copy still works */
    }
  };

  const recallShareUrl = (checklistId, activeShareId) => {
    try {
      const raw = localStorage.getItem(shareCacheKey(checklistId));
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      // Only trust the cache when it belongs to the CURRENTLY active
      // share row - a re-mint rotates share_id and kills stale URLs.
      return parsed?.share_id && parsed.share_id === activeShareId
        ? parsed.url || null
        : null;
    } catch {
      return null;
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const raw = await fetchData(MANAGED_SERVICE.WORKSPACE(requestId));
      const next = raw?.result || raw || null;
      setWs(next);
      // Rehydrate the share link after a refresh: the server never
      // returns the raw URL again, but this browser minted it.
      if (next?.checklist?.checklist_id && next?.share?.usable) {
        const cached = recallShareUrl(
          next.checklist.checklist_id,
          next.share.share_id,
        );
        if (cached) setShareUrl(cached);
      } else {
        setShareUrl(null);
      }
    } catch (e) {
      setError(
        typeof e === "string" ? e : e?.detail || e?.message || "Failed to load",
      );
    } finally {
      setLoading(false);
    }
  }, [requestId]);

  useEffect(() => {
    load();
  }, [load]);

  const request = ws?.request || {};
  const status = request.status || "requested";
  // Routing (04-10-2026): the BE says what this user may do with the request
  // (open / assigned / accepted, and the four actions). Preparation unlocks
  // only after ACCEPT - being assigned is not enough.
  const routing = ws?.routing || {};
  const drafting = ws?.drafting || {};
  const accepted = routing.can_work;
  const terminal = status === "cancelled" || status === "delivered";

  // Accept / disown / capture live in routingActions so this page and the
  // dashboard ask the same questions in the same words.
  const confirmAccept = () =>
    acceptRequestConfirm(requestId, { onDone: load, onBusy: setActing });

  const confirmDisown = () =>
    disownRequestConfirm(requestId, { onDone: load, onBusy: setActing });

  // Administrator release: takes the request off whoever holds it, even once
  // they have accepted it. The accept timeout cannot do this, because it only
  // sweeps requests nobody accepted.
  const confirmRelease = () =>
    releaseRequestConfirm(requestId, {
      holderName: routing.assigned_reviewer_name,
      onDone: load,
      onBusy: setActing,
    });

  const takeRequest = () =>
    captureRequest(requestId, { onDone: load, onBusy: setActing });

  // Administrator: hand the request to a specific review user of its portal.
  const confirmReassign = async () => {
    let target = null;
    let options = [];
    try {
      const raw = await fetchData(MANAGED_SERVICE.REVIEWERS(request.portal));
      options = (raw?.result || raw || {}).reviewers || [];
    } catch {
      options = [];
    }
    if (!options.length) {
      antMessage.warning("No enabled review user handles this portal.");
      return;
    }
    Modal.confirm({
      title: "Reassign this request",
      icon: <SwapOutlined style={{ color: "#4338ca" }} />,
      content: (
        <div className="mt-2">
          <Paragraph className="text-slate-500 text-sm mb-2">
            The reviewer you choose is notified by email and still has to
            accept the request.
          </Paragraph>
          <Select
            className="w-full"
            placeholder="Choose a review user"
            onChange={(v) => {
              target = v;
            }}
            options={options.map((o) => ({
              value: o.id,
              label: `${o.name} (${o.open_requests} open)`,
            }))}
          />
        </div>
      ),
      okText: "Reassign",
      onOk: async () => {
        if (!target) {
          antMessage.warning("Choose a review user first");
          throw new Error("no target");
        }
        setActing(true);
        try {
          await postData(MANAGED_SERVICE.REASSIGN(requestId), { reviewer_id: target });
          antMessage.success("Request reassigned");
          await load();
        } catch (e) {
          antMessage.error(
            typeof e === "string" ? e : e?.detail || e?.message || "Reassign failed",
          );
        } finally {
          setActing(false);
        }
      },
    });
  };

  const confirmReject = () => {
    let reason = "";
    Modal.confirm({
      title: "Reject this request?",
      icon: <CloseCircleOutlined style={{ color: "#dc2626" }} />,
      content: (
        <div className="mt-2">
          <Paragraph className="text-slate-500 text-sm mb-2">
            The request closes and the subscriber sees it as closed. This
            cannot be undone.
          </Paragraph>
          <Input.TextArea
            rows={2}
            placeholder="Reason (optional)"
            onChange={(e) => {
              reason = e.target.value;
            }}
          />
        </div>
      ),
      okText: "Reject",
      okButtonProps: { danger: true },
      onOk: async () => {
        setActing(true);
        try {
          await postData(MANAGED_SERVICE.REJECT(requestId), { reason });
          antMessage.success("Request rejected");
          await load();
        } catch (e) {
          antMessage.error(
            typeof e === "string" ? e : e?.detail || e?.message || "Reject failed",
          );
        } finally {
          setActing(false);
        }
      },
    });
  };

  // Generating the response is the SAME conversation the subscriber has, so
  // the reviewer gets the same component: the terms, the choice of whether to
  // identify legal issues, then the issue picker and whatever the engine
  // reports as missing (05-10-2026). The reviewer's own endpoint does the
  // generation, tags the draft and moves the request, so the page cannot
  // leave a request half-drafted.
  const generateDraft = () => setGenerateFlowOpen(true);

  // Where the finished draft takes the reviewer: straight into its review.
  const onDraftReady = (result) => {
    const sessionId =
      result?.review_session_id || result?.review_session?.session_id;
    antMessage.success("Draft ready for review");
    if (sessionId) {
      navigate(`/response-review/${sessionId}`);
    } else {
      load();
    }
  };

  const openReview = () => {
    if (drafting.review_session_id) {
      navigate(`/response-review/${drafting.review_session_id}`);
    }
  };

  const confirmDeliver = () =>
    Modal.confirm({
      title: "Deliver this response to the subscriber?",
      icon: <CheckCircleOutlined style={{ color: "#16a34a" }} />,
      content:
        "The reviewed response is written to the notice and the subscriber can use it. Every point must be confirmed first.",
      okText: "Deliver",
      onOk: async () => {
        setDraftBusy(true);
        try {
          await postData(MANAGED_SERVICE.DELIVER(requestId), {});
          antMessage.success("Response delivered");
          await load();
        } catch (e) {
          antMessage.error(
            typeof e === "string" ? e : e?.detail || e?.message || "Deliver failed",
          );
        } finally {
          setDraftBusy(false);
        }
      },
    });

  // Open a file the client uploaded against a checklist item. The download
  // endpoint is authenticated, so the token has to travel with the request -
  // the same helper the tenant notice page uses for its attachments.
  const openAttachment = async (file) => {
    try {
      await downloadAuthedFile(
        `${DOCUMENTS.DOWNLOAD_FILE}?file_url=${encodeURIComponent(file.file_url || "")}`,
        file.file_name || "attachment",
      );
    } catch (e) {
      antMessage.error(e?.message || "Could not open the file");
    }
  };

  const prepareChecklist = async () => {
    setPreparing(true);
    try {
      await postData(CHECKLIST.GENERATE, {
        notice_type: request.notice_type,
        notice_id: request.notice_id,
      });
      antMessage.success("Checklist prepared");
      await load();
    } catch (e) {
      antMessage.error(
        typeof e === "string" ? e : e?.detail || e?.message || "Checklist failed",
      );
    } finally {
      setPreparing(false);
    }
  };

  const mintShareLink = async () => {
    setMinting(true);
    try {
      const raw = await postData(CHECKLIST.SHARE(ws.checklist.checklist_id), {});
      const url = raw?.share_url || raw?.result?.share_url;
      if (!url) throw new Error("No link returned");
      const shareId = raw?.share_id || raw?.result?.share_id;
      if (ws?.checklist?.checklist_id && shareId) {
        rememberShareUrl(ws.checklist.checklist_id, shareId, url);
      }
      setShareUrl(url);
      antMessage.success("Share link generated");
      await load();
    } catch (e) {
      antMessage.error(
        typeof e === "string" ? e : e?.detail || e?.message || "Link failed",
      );
    } finally {
      setMinting(false);
    }
  };

  // The notice gets its own page: a reviewer reading a notice needs the room,
  // and the page shows every field plus the attachments (05-10-2026).
  const openNotice = () => navigate(`/review-requests/${requestId}/notice`);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      antMessage.success("Link copied");
    } catch {
      antMessage.info(shareUrl);
    }
  };

  const shareText = shareUrl
    ? `Hello, please upload the documents required for your tax notice response here: ${shareUrl} (the link expires ${ws?.share?.open_window_minutes || 30} minutes after you first open it).`
    : "";

  const shareWhatsApp = () =>
    window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, "_blank");

  const shareEmail = () =>
    window.open(
      `mailto:?subject=${encodeURIComponent(
        "Documents required for your tax notice response",
      )}&body=${encodeURIComponent(shareText)}`,
      "_blank",
    );

  const openWaSend = () => {
    setWaSendTo(ws?.requester?.phone_number || "");
    setWaSendOpen(true);
  };

  const sendWhatsApp = async () => {
    const to = (waSendTo || "").trim();
    if (!/^\+?[0-9][0-9\s-]{5,20}$/.test(to)) {
      antMessage.error(
        "Enter a valid phone number with country code, e.g. +919876543210.",
      );
      return;
    }
    const checklistId = ws?.checklist?.checklist_id;
    if (!checklistId || !shareUrl) return;
    setWaSending(true);
    try {
      const res = await postData(CHECKLIST.SHARE_WHATSAPP(checklistId), {
        to_number: to,
        share_url: shareUrl,
      });
      antMessage.success(
        `Checklist link sent on WhatsApp to ${res?.to || to} using the ${res?.template || "configured"} template.`,
      );
      setWaSendOpen(false);
    } catch (e) {
      // Keep the modal open so the reviewer can correct the number and retry.
      antMessage.error(
        (typeof e === "string" && e) ||
          e?.detail ||
          e?.message ||
          "WhatsApp send failed.",
      );
    } finally {
      setWaSending(false);
    }
  };

  if (loading && !ws) {
    return (
      <Card>
        <Skeleton active paragraph={{ rows: 8 }} />
      </Card>
    );
  }
  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        message="Failed to load the request"
        description={error}
      />
    );
  }
  if (!ws) return null;

  const meta = STATUS_META[status] || { color: "default", label: status };
  const fields =
    NOTICE_FIELD_LABELS[request.notice_type] || [["client_name", "Client"]];

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <Space className="mb-1">
            <Link to="/review-dashboard">
              <Button size="small" icon={<ArrowLeftOutlined />} type="text">
                Review Dashboard
              </Button>
            </Link>
          </Space>
          <Title level={3} className="mb-1!">
            Response Generation Request
          </Title>
          <Paragraph className="mb-0! text-slate-500">
            {ws.organization_name} · {request.notice_type}
          </Paragraph>
        </div>
        <Space>
          <Tag color={meta.color} className="px-3 py-1 text-sm">
            {meta.label}
          </Tag>
          <Button icon={<ReloadOutlined />} onClick={load}>
            Refresh
          </Button>
        </Space>
      </div>

      {/* ── Notice basic details ── */}
      <Card title="Notice Details" className="mb-4">
        {ws.notice_missing ? (
          <Text type="secondary">
            The notice behind this request is no longer available.
          </Text>
        ) : (
          <Descriptions size="small" column={{ xs: 1, sm: 2 }} bordered>
            {fields.map(([key, label]) => (
              <Descriptions.Item key={key} label={label}>
                {ws.notice?.[key] === "" || ws.notice?.[key] == null
                  ? "-"
                  : String(ws.notice[key])}
              </Descriptions.Item>
            ))}
            <Descriptions.Item label="Requested">
              {fmtDateTime(request.requested_at)}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Card>

      {/* ── Routing: who holds this request, and what I may do with it ── */}
      {!terminal && (
        <Card className="mb-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Space size="middle" wrap>
              <Tag color={ROUTING_TAGS[routing.state]?.color || "default"}>
                {ROUTING_TAGS[routing.state]?.label || "-"}
              </Tag>
              <Text type="secondary" className="text-sm">
                {routing.state === "open"
                  ? "Open to every reviewer of this portal."
                  : routing.assigned_to_me
                    ? routing.state === "accepted"
                      ? "You accepted this request."
                      : "Assigned to you - accept it to start work."
                    : `With ${routing.assigned_reviewer_name || "another reviewer"}.`}
              </Text>
              {/* Who had it before. Only an administrator is sent this, and
                  it is what they need before deciding where an open request
                  should go: the previous holder already had their chance. */}
              {routing.previous_reviewer_name && (
                <Tag icon={<UndoOutlined />} color="default">
                  previously with {routing.previous_reviewer_name}
                </Tag>
              )}
              {routing.state === "assigned" && routing.accept_deadline && (
                <Tooltip
                  title={`Not accepted by ${fmtDateTime(routing.accept_deadline)} means it goes back to the pool for any reviewer of this portal to take, and an administrator is told`}
                >
                  <Tag icon={<ClockCircleOutlined />} color="orange">
                    accept by {fmtDateTime(routing.accept_deadline)}
                  </Tag>
                </Tooltip>
              )}
            </Space>
            <Space size="middle" wrap>
              {routing.can_capture && (
                <Button
                  type="primary"
                  className="header-matched-primary"
                  icon={<ThunderboltOutlined />}
                  loading={acting}
                  onClick={takeRequest}
                >
                  I'm working on it
                </Button>
              )}
              {routing.can_accept && (
                <Button
                  type="primary"
                  className="header-matched-primary"
                  icon={<CheckCircleOutlined />}
                  loading={acting}
                  onClick={confirmAccept}
                >
                  Accept
                </Button>
              )}
              {routing.can_disown && (
                <Button icon={<UndoOutlined />} loading={acting} onClick={confirmDisown}>
                  Disown
                </Button>
              )}
              {routing.can_reassign && (
                <Button icon={<SwapOutlined />} loading={acting} onClick={confirmReassign}>
                  Reassign
                </Button>
              )}
              {/* Release is the administrator's version of Disown. Hidden when
                  Disown is already on screen, so one request never offers two
                  buttons that call the same endpoint. */}
              {routing.can_release && !routing.can_disown && (
                <Button icon={<UndoOutlined />} loading={acting} onClick={confirmRelease}>
                  Release
                </Button>
              )}
              {routing.can_reject && (
                <Button
                  danger
                  icon={<CloseCircleOutlined />}
                  loading={acting}
                  onClick={confirmReject}
                >
                  Reject
                </Button>
              )}
            </Space>
          </div>
        </Card>
      )}

      {(ws?.timeline || []).length > 0 && (
        <Card title="History" className="mb-4" size="small">
          <Steps
            direction="vertical"
            size="small"
            current={-1}
            items={ws.timeline.map((t) => ({
              status: "finish",
              title: TIMELINE_LABELS[t.action] || t.action.replace(/_/g, " "),
              description: (
                <Text type="secondary" className="text-xs">
                  {fmtDateTime(t.at)}
                  {t.to ? ` - to ${t.to}` : ""}
                  {t.by ? ` - by ${t.by}` : ""}
                  {t.source ? ` (${t.source})` : ""}
                </Text>
              ),
            }))}
          />
        </Card>
      )}

      {status === "cancelled" && (
        <Alert
          type="warning"
          showIcon
          className="mb-4"
          message="This request was rejected and is closed."
        />
      )}

      {/* ── Post-accept tools ── */}
      {accepted && (
        <Card title="Preparation" className="mb-4">
          <Space size="middle" wrap>
            <Button
              icon={<FileDoneOutlined />}
              loading={preparing}
              onClick={prepareChecklist}
            >
              {ws.checklist
                ? "Regenerate Required File/Attachment Checklist"
                : "Prepare Required File/Attachment Checklist"}
            </Button>
            <Button icon={<FileSearchOutlined />} onClick={openNotice}>
              Open Notice
            </Button>
          </Space>

          {ws.checklist && (
            <div className="mt-4 pt-4 border-t border-slate-100">
              <div
                className="flex items-center justify-between flex-wrap gap-3 mb-3 px-4 py-3"
                style={{
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: 8,
                }}
              >
                <Space size="middle" wrap>
                  <Text strong className="text-base">
                    Required File Checklist
                  </Text>
                  <Tag
                    color="geekblue"
                    className="m-0!"
                    style={{ borderRadius: 999, fontWeight: 600 }}
                  >
                    {ws.checklist.provided_count}/{ws.checklist.item_count} provided
                  </Tag>
                </Space>
                <Progress
                  percent={
                    ws.checklist.item_count
                      ? Math.round(
                          (ws.checklist.provided_count /
                            ws.checklist.item_count) *
                            100,
                        )
                      : 0
                  }
                  size="small"
                  style={{ width: 180, margin: 0 }}
                  strokeColor="#1e3a8a"
                />
              </div>

              {/* The items themselves — numbered cards with a status
                  accent so the reviewer scans the ask at a glance:
                  green = provided, red edge = mandatory pending,
                  slate = optional pending. */}
              <div className="mb-4" style={{ display: "grid", gap: 8 }}>
                {(ws.checklist.items || []).map((item, idx) => {
                  const done =
                    item.status === "provided" ||
                    item.status === "not_applicable";
                  const accent = done
                    ? "#16a34a"
                    : item.mandatory
                      ? "#dc2626"
                      : "#94a3b8";
                  return (
                    <div
                      key={item.item_id || idx}
                      className="flex items-start gap-3 px-4 py-3"
                      style={{
                        background: done ? "#f0fdf4" : "#ffffff",
                        border: "1px solid #e2e8f0",
                        borderLeft: `3px solid ${accent}`,
                        borderRadius: 8,
                      }}
                    >
                      <div
                        style={{
                          width: 26,
                          height: 26,
                          borderRadius: "50%",
                          flexShrink: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 13,
                          fontWeight: 600,
                          marginTop: 2,
                          background: done ? "#dcfce7" : "#eef2ff",
                          color: done ? "#16a34a" : "#1e3a8a",
                        }}
                      >
                        {done ? <CheckCircleOutlined /> : idx + 1}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                          <Text strong={!done}>{item.title}</Text>
                          {done ? (
                            <Tag
                              color="green"
                              className="m-0!"
                              style={{ borderRadius: 999 }}
                            >
                              Provided
                            </Tag>
                          ) : item.mandatory ? (
                            <Tag
                              color="red"
                              className="m-0!"
                              style={{ borderRadius: 999 }}
                            >
                              Required
                            </Tag>
                          ) : (
                            <Tag className="m-0!" style={{ borderRadius: 999 }}>
                              Optional
                            </Tag>
                          )}
                        </div>
                        {item.why_needed && (
                          <Text
                            type="secondary"
                            className="text-xs block mt-1"
                            style={{ lineHeight: 1.5 }}
                          >
                            {item.why_needed}
                          </Text>
                        )}
                        {(item.files || []).length > 0 && (
                          <Space className="mt-2" size={8} wrap>
                            {item.files.map((f, i) => (
                              <Button
                                key={`${f.file_url}-${i}`}
                                size="small"
                                icon={<PaperClipOutlined />}
                                onClick={() => openAttachment(f)}
                              >
                                {f.file_name}
                              </Button>
                            ))}
                          </Space>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-2">
                {shareUrl ? (
                  <div>
                    <Space className="mb-2" wrap>
                      <Tag icon={<LinkOutlined />} color="green" className="px-2 py-1">
                        Public upload link ready
                      </Tag>
                      <Text type="secondary" className="text-xs">
                        Expires {ws.share?.open_window_minutes || 30} minutes
                        after the subscriber first opens it.
                      </Text>
                    </Space>
                    <Space.Compact style={{ width: "100%", maxWidth: 560 }}>
                      <Input readOnly value={shareUrl} />
                      <Tooltip title="Copy link">
                        <Button icon={<CopyOutlined />} onClick={copyLink} />
                      </Tooltip>
                    </Space.Compact>
                    <Space className="mt-3" wrap>
                      <Tooltip title="Sends the link to the subscriber's number through the approved WhatsApp template. Needs the WhatsApp account configured under System Configuration.">
                        <Button
                          type="primary"
                          className="header-matched-primary"
                          icon={<WhatsAppOutlined />}
                          style={{ background: "#16a34a", borderColor: "#16a34a" }}
                          onClick={openWaSend}
                        >
                          Send via WhatsApp
                        </Button>
                      </Tooltip>
                      <Button
                        icon={<WhatsAppOutlined />}
                        style={{ color: "#16a34a", borderColor: "#16a34a" }}
                        onClick={shareWhatsApp}
                      >
                        Share manually
                      </Button>
                      <Button icon={<MailOutlined />} onClick={shareEmail}>
                        Share via Email
                      </Button>
                    </Space>
                  </div>
                ) : (
                  <Space wrap>
                    <Button
                      type="primary"
                      className="header-matched-primary"
                      icon={<LinkOutlined />}
                      loading={minting}
                      onClick={mintShareLink}
                    >
                      {ws.share?.usable
                        ? "Generate New Share Link"
                        : "Generate Share Link"}
                    </Button>
                    {ws.share?.usable && (
                      <Text type="secondary" className="text-xs">
                        A link is already live
                        {ws.share.first_opened_at
                          ? " and has been opened"
                          : " (not opened yet)"}
                        . Generating a new one replaces it.
                      </Text>
                    )}
                  </Space>
                )}
              </div>
            </div>
          )}
        </Card>
      )}

      {/* ── Drafting: generate, review, deliver ── */}
      {accepted && !terminal && (
        <Card title="Response" className="mb-4">
          {!drafting.documents_ready && !drafting.review_session_id ? (
            <Alert
              type="info"
              showIcon
              message="Waiting for the required documents"
              description="Generate Response unlocks once every required item on the checklist above is provided."
            />
          ) : (
            <Space size="middle" wrap>
              {drafting.can_draft && (
                <Button
                  type="primary"
                  className="header-matched-primary"
                  icon={<FileTextOutlined />}
                  loading={draftBusy}
                  onClick={generateDraft}
                >
                  Generate Response
                </Button>
              )}
              {drafting.can_review && (
                <Button
                  type={drafting.can_deliver ? "default" : "primary"}
                  className={drafting.can_deliver ? "" : "header-matched-primary"}
                  icon={<FileSearchOutlined />}
                  onClick={openReview}
                >
                  {drafting.review_status === "confirmed"
                    ? "Reopen review"
                    : "Review the draft"}
                </Button>
              )}
              {drafting.can_review && (
                <Tooltip
                  title={
                    drafting.can_deliver
                      ? "Write the reviewed response to the notice"
                      : "Confirm every point in the review first"
                  }
                >
                  <Button
                    type={drafting.can_deliver ? "primary" : "default"}
                    className={drafting.can_deliver ? "header-matched-primary" : ""}
                    icon={<CheckCircleOutlined />}
                    disabled={!drafting.can_deliver}
                    loading={draftBusy}
                    onClick={confirmDeliver}
                  >
                    Deliver to subscriber
                  </Button>
                </Tooltip>
              )}
            </Space>
          )}
        </Card>
      )}

      {status === "delivered" && (
        <Alert
          type="success"
          showIcon
          className="mb-4"
          message="This response was delivered to the subscriber."
        />
      )}

      {/* ── Send checklist link via WhatsApp (approved template) ── */}
      <Modal
        title="Send checklist link via WhatsApp"
        open={waSendOpen}
        onOk={sendWhatsApp}
        confirmLoading={waSending}
        okText="Send"
        onCancel={() => setWaSendOpen(false)}
        destroyOnClose
      >
        <p className="text-slate-500 text-sm mb-2">
          The upload link goes to this number through the approved
          "required documents checklist" WhatsApp template, with the
          client name, notice and link validity filled in.
          {ws?.requester?.name ? ` Pre-filled from ${ws.requester.name}'s profile.` : ""}
        </p>
        <Input
          placeholder="+919876543210"
          value={waSendTo}
          onChange={(e) => setWaSendTo(e.target.value)}
          onPressEnter={sendWhatsApp}
          autoFocus
          allowClear
        />
      </Modal>

      {/* The subscriber's generate-response conversation, rendered here
          against the reviewer's own endpoint. `legalChoiceEnabled` is on
          unconditionally: the plan gate that hides it for a subscriber is
          the customer's, and a NABS reviewer drafts on their behalf. */}
      <GenerateResponseFlow
        open={generateFlowOpen}
        onClose={() => setGenerateFlowOpen(false)}
        legalChoiceEnabled
        generateUrl={MANAGED_SERVICE.DRAFT(requestId)}
        statusUrl={(taskId) => MANAGED_SERVICE.DRAFT_STATUS(requestId, taskId)}
        resumeKey={`review_draft_task_${requestId}`}
        onApplied={onDraftReady}
      />
    </div>
  );
}
